import React from 'react'
import { Typography, Layout, Divider, Table } from 'antd'

const { Title, Paragraph, Text } = Typography

const privacyColumns = [
  { title: 'Purpose', dataIndex: 'purpose', key: 'purpose', render: (text: string) => <Text strong>{text}</Text> },
  { title: 'Legal basis', dataIndex: 'basis', key: 'basis' },
]

const privacyData = [
  { key: '1', purpose: 'Customer account management', basis: 'Performance of a contract (Art. 6.1(b) GDPR)' },
  { key: '2', purpose: 'Subscription management and billing', basis: 'Legal obligation' },
  { key: '3', purpose: 'Technical support', basis: 'Legitimate interest' },
  { key: '4', purpose: 'Security and prevention of abuse', basis: 'Legitimate interest' },
  { key: '5', purpose: 'Improvement of services', basis: 'Legitimate interest' },
  { key: '6', purpose: 'Regulatory compliance', basis: 'Legal obligation' },
]

const PrivacyPolicyEN = () => (
  <Layout style={{ padding: '1.5em 0.75em' }}>
    <Typography>
      <Title level={2} style={{ textAlign: 'center', textTransform: 'uppercase' }}>
        PRIVACY POLICY
      </Title>
      <Paragraph style={{ textAlign: 'center' }}>
        <Text strong>Web and Mobile Application “MusicTMM-Universum”</Text>
      </Paragraph>
      <Paragraph style={{ textAlign: 'center' }}>
        <Text type="secondary">For users in the United States, Canada and the United Kingdom</Text>
      </Paragraph>
      <Paragraph style={{ textAlign: 'center' }}>
        <Text type="secondary">Effective date: July 19 2026</Text>
      </Paragraph>

      <Divider />

      <Paragraph>
        This Privacy Policy describes how <Text strong>MusicTMM SAS</Text> (“we,” “us,” “our,” or the “Company”) collects, uses, discloses and protects personal information in connection with the “MusicTMM-Universum” web and mobile platform (the “Platform”). It applies to users located in the United States, Canada and the United Kingdom.
      </Paragraph>
      <Paragraph>
        We operate strictly on a business-to-business (B2B) basis. We do not collect personal information relating to the end customers or patrons of the establishments that use the Platform.
      </Paragraph>

      <Title level={4}>1. Data Controller / Business Contact</Title>
      <Paragraph>
        The entity responsible for your personal information (the “data controller,” and the “business” for the purposes of U.S. state privacy laws) is:
      </Paragraph>
      <Paragraph>
        <Text strong>MusicTMM SAS</Text>
        <br />
        99 Avenue Achille Peretti, 92200 Neuilly-sur-Seine, France
        <br />
        Email: <Text strong>hello@musictmm.fr</Text>
      </Paragraph>
      <Paragraph>
        MusicTMM SAS acts as the sole controller of personal information collected through the Platform. On certain territories, the Platform’s services may be provided by an affiliated company or an authorized local partner (as described in the Terms of Use); where any such partner processes personal information on our behalf, it acts as our service provider / processor under a written agreement and does not become an independent controller of your information.
      </Paragraph>

      <Title level={4}>2. Categories of Personal Information We Collect</Title>
      <Title level={5}>2.1 Professional identification data</Title>
      <ul>
        <li>professional email address</li>
        <li>company name</li>
        <li>name of representative</li>
        <li>business address</li>
        <li>professional telephone number</li>
      </ul>
      <Title level={5}>2.2 Technical data</Title>
      <ul>
        <li>IP address</li>
        <li>device type and model</li>
        <li>operating system</li>
        <li>connection logs</li>
        <li>usage history</li>
      </ul>
      <Title level={5}>2.3 Location data</Title>
      <ul>
        <li>approximate location (country, city)</li>
      </ul>
      <Paragraph>
        We do not collect precise geolocation (GPS) data without your explicit consent. We do not process special/sensitive categories of information, and we do not sell your personal information or share it for cross-context behavioral advertising. We do not carry out profiling or automated decision-making that produces legal or similarly significant effects.
      </Paragraph>

      <Title level={4}>3. How and Why We Use Personal Information</Title>
      <Paragraph>
        We use personal information for the following business purposes:
      </Paragraph>
      <ul>
        <li><Text strong>Customer account management</Text> — to create and administer professional accounts</li>
        <li><Text strong>Subscription management and billing</Text> — to process orders, invoicing and payments</li>
        <li><Text strong>Technical support</Text> — to respond to requests and resolve issues</li>
        <li><Text strong>Security and prevention of abuse</Text> — to protect the Platform and prevent fraud</li>
        <li><Text strong>Improvement of services</Text> — to maintain and improve our services</li>
        <li><Text strong>Regulatory compliance</Text> — to comply with applicable legal obligations</li>
      </ul>
      <Paragraph>
        Where required by the UK GDPR, our legal bases are: performance of a contract; compliance with a legal obligation; and our legitimate interests (including the security of the Platform, fraud prevention and improvement of services). Where processing relies on legitimate interests, you may object on grounds relating to your particular situation.
      </Paragraph>
      <Table columns={privacyColumns} dataSource={privacyData} pagination={false} bordered />

      <Title level={4}>4. Service Providers and Disclosure</Title>
      <Paragraph>
        We may share personal information with technical service providers acting on our behalf, including cloud hosting providers, technical service providers and maintenance services. These providers are bound by written agreements that restrict their use of the information to the services they perform for us. A list of our service providers is available on request.
      </Paragraph>
      <Paragraph>
        We may also disclose personal information where required by law, to comply with legal process, or to protect our rights, users or the public. We do not sell personal information and do not share it for targeted or cross-context behavioral advertising.
      </Paragraph>

      <Title level={4}>5. International Data Transfers</Title>
      <Paragraph>
        Personal information is hosted within the European Union. Because MusicTMM SAS is established in France, information collected from users in the United States, Canada and the United Kingdom is transferred to and processed in the European Union and, where applicable, other countries. Where such transfers occur, they are governed by appropriate safeguards, including the Standard Contractual Clauses (including the UK International Data Transfer Addendum, where applicable) or another mechanism recognized as providing an adequate level of protection.
      </Paragraph>

      <Title level={4}>6. Retention Period</Title>
      <Paragraph>
        We retain personal information for the duration of the contractual relationship, after which it is archived in accordance with applicable legal obligations (in particular accounting obligations). Billing data is retained for ten (10) years; technical logs are retained for a maximum of twelve (12) months; thereafter, information is deleted or anonymized.
      </Paragraph>

      <Title level={4}>7. Security</Title>
      <Paragraph>
        We implement appropriate technical and organizational measures designed to protect personal information, including encryption of data in transit, restricted access control, access logging and regular backups. No method of transmission or storage is completely secure; we undertake to use commercially reasonable efforts to protect your information.
      </Paragraph>

      <Title level={4}>8. Your Privacy Rights</Title>
      <Title level={5}>8.1 All users</Title>
      <Paragraph>
        Depending on where you live, you may have some or all of the following rights: the right to access your personal information; to correct or rectify it; to request its deletion or erasure; to restrict or object to certain processing; and to data portability. To exercise any of these rights, contact us at <Text strong>hello@musictmm.fr</Text>. We will respond within the period required by applicable law. We will not discriminate against you for exercising your rights.
      </Paragraph>
      <Title level={5}>8.2 United States (California and other state laws)</Title>
      <Paragraph>
        If you are a resident of California or another U.S. state with a comprehensive privacy law, you may have the right to know what personal information we collect and how we use it, to access and delete it, to correct inaccuracies, and to opt out of any “sale” or “sharing” of personal information. We do not sell or share personal information as those terms are defined under the California Consumer Privacy Act (CCPA), as amended by the CPRA. You may exercise your rights, including through an authorized agent, by contacting us at <Text strong>hello@musictmm.fr</Text>.
      </Paragraph>
      <Title level={5}>8.3 Canada (PIPEDA)</Title>
      <Paragraph>
        If you are located in Canada, you may access and request correction of your personal information and may withdraw consent, subject to legal or contractual restrictions. You may also contact the Office of the Privacy Commissioner of Canada.
      </Paragraph>
      <Title level={5}>8.4 United Kingdom (UK GDPR)</Title>
      <Paragraph>
        If you are located in the United Kingdom, you have the rights described above under the UK GDPR, and you may lodge a complaint with the Information Commissioner’s Office (ICO) at ico.org.uk.
      </Paragraph>

      <Title level={4}>9. Data Breach</Title>
      <Paragraph>
        In the event of a personal data breach that may pose a risk to the rights and freedoms of individuals, we will notify the competent supervisory authority and, where required, the individuals concerned, in accordance with applicable law.
      </Paragraph>

      <Title level={4}>10. Children</Title>
      <Paragraph>
        The Platform is intended solely for businesses and professionals and is not directed to children. We do not knowingly collect personal information from children.
      </Paragraph>

      <Title level={4}>11. Changes to This Policy</Title>
      <Paragraph>
        We may update this Privacy Policy at any time to reflect regulatory or technical developments. Any substantial change will be notified via the Platform or by email, and the “Effective date” above will be updated.
      </Paragraph>

      <Title level={4}>12. Contact Us</Title>
      <Paragraph>
        For any question about this Privacy Policy or to exercise your rights, contact: <Text strong>hello@musictmm.fr</Text>
      </Paragraph>
    </Typography>
  </Layout>
)

export default PrivacyPolicyEN
